import { useEffect, useRef } from "react";
import { toast } from "sonner";

export function useJoinLeaveToasts({
    connected,
    liveOnlyParticipants,
    resolvedTableName,
    currentTableId,
}) {
    const cleanName = (v) => {
        const s = String(v ?? "").trim();
        if (!s) return null;

        const bad = ["none", "null", "undefined", "nan", "-", "unknown"];
        if (bad.includes(s.toLowerCase())) return null;

        return s;
    };

    const prevLiveMapRef = useRef(new Map()); // id -> name
    const didInitLiveToastRef = useRef(false);
    const toastReadyRef = useRef(false);
    const toastWarmupTimerRef = useRef(null);

    useEffect(() => {
        toastReadyRef.current = false;
        didInitLiveToastRef.current = false;
        prevLiveMapRef.current = new Map();

        if (toastWarmupTimerRef.current) {
            clearTimeout(toastWarmupTimerRef.current);
            toastWarmupTimerRef.current = null;
        }

        if (!connected) return;

        toastWarmupTimerRef.current = setTimeout(() => {
            toastReadyRef.current = true;
            didInitLiveToastRef.current = false;
        }, 2500);

        return () => {
            if (toastWarmupTimerRef.current) {
                clearTimeout(toastWarmupTimerRef.current);
                toastWarmupTimerRef.current = null;
            }
        };
    }, [connected, currentTableId]);

    useEffect(() => {
        if (!connected) return;

        const nextMap = new Map(
            liveOnlyParticipants.map((p) => [p.id, cleanName(p.name) || "Someone"]),
        );

        // warmup: baseline only, no toasts
        if (!toastReadyRef.current) {
            prevLiveMapRef.current = nextMap;
            return;
        }

        const prevMap = prevLiveMapRef.current;

        // first run after warmup: baseline only, no toasts
        if (!didInitLiveToastRef.current) {
            didInitLiveToastRef.current = true;
            prevLiveMapRef.current = nextMap;
            return;
        }

        // JOINED
        for (const [id, name] of nextMap) {
            const p = liveOnlyParticipants.find((x) => x.id === id);
            if (p?.isLocal) continue;
            if (!prevMap.has(id)) {
                toast.success(`${name} joined the meeting room in ${resolvedTableName}`);
            }
        }

        // LEFT
        for (const [id, name] of prevMap) {
            if (!nextMap.has(id)) {
                toast(`${name} left ${resolvedTableName}`, { icon: "👋" });
            }
        }

        prevLiveMapRef.current = nextMap;
    }, [connected, liveOnlyParticipants, resolvedTableName, currentTableId]);
}